/**
 * The catering assortment — the first and main branch of Småken.
 *
 * Two vocabularies, deliberately kept apart:
 *   OCCASION  — when you eat it (Frukost, Fika, Lunch, Mellanmål, Middag)
 *   CATEGORY  — what kind of thing it is (Mat, Dryck, Bak, Snacks)
 *
 * A partner picks one category per item and any number of occasions, which is
 * how a fikakorg can be both Fika and Mellanmål without needing two entries.
 * Every partner carries ten items — that is the number we ask for at onboarding.
 */

/** When it is eaten. This is what the visitor picks first. */
export type Occasion = 'Frukost' | 'Fika' | 'Lunch' | 'Mellanmål' | 'Middag';

export const OCCASIONS: Occasion[] = ['Frukost', 'Fika', 'Lunch', 'Mellanmål', 'Middag'];

/** What kind of thing it is. */
export type CateringCategory = 'Mat' | 'Dryck' | 'Bak' | 'Snacks';

export const CATERING_CATEGORIES: CateringCategory[] = ['Mat', 'Dryck', 'Bak', 'Snacks'];

export type CateringUnit = 'per person' | 'per styck' | 'per bricka';

export interface CateringItem {
  id: string;
  name: string;
  category: CateringCategory;
  occasions: Occasion[];
  description: string;
  /** Price in SEK, excl. VAT, for one unit of `unit`. */
  price: number;
  unit: CateringUnit;
  minGuests: number;
  /** Days of notice the partner needs. */
  leadTime: number;
  image: string;
}

const IMG = {
  frukost: 'https://cdn.magicpatterns.com/patterns/generated-images/0c664b63-8d57-4f98-a722-f14bd09f5a50.jpg',
  bullar: 'https://cdn.magicpatterns.com/patterns/generated-images/264b58b6-1c66-481c-9dc4-a443e6930eeb.jpg',
  fika: 'https://cdn.magicpatterns.com/patterns/generated-images/eebd77e7-cfe4-4b68-804b-72efaefe699e.jpg',
  lunch: 'https://cdn.magicpatterns.com/patterns/generated-images/65d70530-8847-48ba-bca4-19be707e197e.jpg',
  dessert: 'https://cdn.magicpatterns.com/patterns/generated-images/8fc743b5-2ac3-4a58-8c38-642530320605.jpg',
  smorgastarta: 'https://cdn.magicpatterns.com/patterns/generated-images/e82a00d0-390c-4001-b6b6-932d937dfd27.jpg',
  tarta: 'https://cdn.magicpatterns.com/patterns/generated-images/b2fc99a2-e202-40f5-8e13-d1a02111b749.jpg',
  buffe: 'https://cdn.magicpatterns.com/patterns/generated-images/eb3d836c-650b-45a4-9ab8-2b946d2a1f10.jpg',
  mingel: 'https://cdn.magicpatterns.com/patterns/generated-images/e2a2da3a-b1f1-4c6e-9b1c-980e29940562.jpg',
  ost: 'https://cdn.magicpatterns.com/patterns/generated-images/6e45c36e-5349-42b8-a31b-caf183124798.jpg',
  gronsaker: 'https://cdn.magicpatterns.com/patterns/generated-images/e3835e73-b6e3-462a-b7fd-0630f4ddbf9a.jpg',
  kaffe: 'https://cdn.magicpatterns.com/patterns/generated-images/8b1aea01-7003-4539-af32-7d71f1ddddf8.jpg',
  barista: 'https://cdn.magicpatterns.com/patterns/generated-images/73db5e3b-fdcd-4519-9a53-96913e9fba08.jpg',
  ol: 'https://cdn.magicpatterns.com/patterns/generated-images/7d403927-1c90-4448-b890-d0f7d2b01b2f.jpg',
  skaldjur: 'https://cdn.magicpatterns.com/patterns/generated-images/478e1add-8a86-4c2d-9fbd-69c95f638067.jpg',
  bageri: 'https://cdn.magicpatterns.com/patterns/generated-images/8303ae1b-9d0f-45da-b556-99c68efa3216.jpg',
  bakverk: 'https://cdn.magicpatterns.com/patterns/generated-images/96991e69-59d8-42b1-a2ff-3d943c1f0a9d.jpg',
  gard: 'https://cdn.magicpatterns.com/patterns/generated-images/31bd1e7b-cfc1-4450-8dbf-b3501fecbec0.jpg',
};

/** Terse constructor so ten items per partner stays readable. */
const i = (
  id: string,
  name: string,
  category: CateringCategory,
  occasions: Occasion[],
  price: number,
  unit: CateringUnit,
  minGuests: number,
  leadTime: number,
  image: string,
  description: string
): CateringItem => ({ id, name, category, occasions, price, unit, minGuests, leadTime, image, description });

/** What each partner can send out. Keyed by place slug, ten items each. */
export const CATERING: Record<string, CateringItem[]> = {
  'lilla-verket': [
    i('lv-1', 'Frukostbricka med surdeg', 'Mat', ['Frukost'], 115, 'per person', 6, 2, IMG.frukost,
      'Nybakad surdeg, smör, ost, marmelad, kokt ägg och frukt. Levereras på träbricka.'),
    i('lv-2', 'Bullbricka till kontoret', 'Bak', ['Fika', 'Mellanmål'], 340, 'per bricka', 1, 1, IMG.bullar,
      'Kanel- och kardemummabullar, nygräddade samma morgon. Tolv bullar per bricka.'),
    i('lv-3', 'Croissanter, nybakade', 'Bak', ['Frukost', 'Fika'], 42, 'per styck', 10, 2, IMG.bakverk,
      'Smör från Sörgården, tjugosju lager. Levereras varma om ni ligger inom fem minuter.'),
    i('lv-4', 'Surdegslimpor till mötet', 'Bak', ['Frukost', 'Lunch'], 75, 'per styck', 3, 2, IMG.bageri,
      'Svenskt lantvete, ett dygns jäsning. Skivas på plats eller hel med kniv i.'),
    i('lv-5', 'Smörgåsbricka på surdeg', 'Mat', ['Lunch', 'Mellanmål'], 95, 'per person', 8, 2, IMG.lunch,
      'Fyra sorter, halva vegetariska. Skurna i halvor så de går att ta med en hand.'),
    i('lv-6', 'Bryggkaffe på termos', 'Dryck', ['Frukost', 'Fika'], 32, 'per person', 8, 1, IMG.kaffe,
      'Böna från Kalmar Kafferosteri. Muggar, mjölk och socker följer med.'),
    i('lv-7', 'Kardemummasnurror', 'Bak', ['Fika'], 38, 'per styck', 12, 1, IMG.bullar,
      'Grovstött kardemumma, pärlsocker. Den vi själva äter för många av.'),
    i('lv-8', 'Eftermiddagsbricka', 'Snacks', ['Mellanmål'], 78, 'per person', 8, 2, IMG.fika,
      'Kaka, frukt och nötter för klockan tre, när mötet drar över.'),
    i('lv-9', 'Ostbricka med vårt bröd', 'Snacks', ['Mellanmål', 'Middag'], 135, 'per person', 8, 3, IMG.ost,
      'Fyra ostar från trakten, kex, honung och en limpa till.'),
    i('lv-10', 'Brödprenumeration', 'Bak', ['Frukost', 'Lunch'], 260, 'per bricka', 1, 3, IMG.bageri,
      'Fyra limpor i veckan till kontoret, samma dag och tid. Sägs upp när ni vill.'),
  ],
  'top-notch-bakery': [
    i('tn-1', 'Fikakorg Deluxe', 'Bak', ['Fika', 'Mellanmål'], 95, 'per person', 8, 1, IMG.fika,
      'Veckans specialbulle, två bakverk per person, frukt och bryggkaffe på termos.'),
    i('tn-2', 'Lunchlådor, dagens', 'Mat', ['Lunch'], 135, 'per person', 10, 2, IMG.lunch,
      'Varm rätt, sallad och bröd i återvinningsbar låda. Vegetariskt ingår alltid.'),
    i('tn-3', 'Dessertbord', 'Bak', ['Middag'], 165, 'per person', 20, 5, IMG.dessert,
      'Sex sorters bakverk uppdukat på plats. Vi kommer, dukar och tar med oss allt efteråt.'),
    i('tn-4', 'Frukostleverans till kontoret', 'Mat', ['Frukost'], 125, 'per person', 8, 2, IMG.frukost,
      'Bröd, pålägg, yoghurt, granola och juice. Står på bordet innan ni kommer.'),
    i('tn-5', 'Veckans specialbulle', 'Bak', ['Fika'], 45, 'per styck', 10, 1, IMG.bullar,
      'Byts varje måndag. Ni får veta vad det blir dagen innan, inte tidigare.'),
    i('tn-6', 'Salladsbowls', 'Mat', ['Lunch'], 145, 'per person', 8, 2, IMG.gronsaker,
      'Fyra fasta bowls, dressing separat. Håller sig hela vägen till klockan ett.'),
    i('tn-7', 'Smörgåstårta till avdelningen', 'Mat', ['Lunch', 'Mellanmål'], 98, 'per person', 10, 3, IMG.smorgastarta,
      'Räka, skinka eller vegetarisk. Levereras skuren och skyltad.'),
    i('tn-8', 'Tårta på beställning', 'Bak', ['Fika', 'Middag'], 265, 'per styck', 1, 3, IMG.tarta,
      'Er text på locket. Sex till åtta personer per tårta.'),
    i('tn-9', 'Mingelbricka, salt', 'Snacks', ['Mellanmål', 'Middag'], 110, 'per person', 12, 3, IMG.mingel,
      'Små matbröd, chark, pickles och dipp. Går att äta stående.'),
    i('tn-10', 'Kaffe & te till leveransen', 'Dryck', ['Frukost', 'Fika'], 30, 'per person', 10, 1, IMG.kaffe,
      'Termoskaffe, tesortiment och mjölk. Läggs till vilken leverans som helst.'),
  ],
  askelyckan: [
    i('as-1', 'Smörgåstårta räka & lax', 'Mat', ['Lunch', 'Mellanmål'], 95, 'per person', 8, 3, IMG.smorgastarta,
      'Klassisk smörgåstårta på ljust bröd. Beräkna en bit per person.'),
    i('as-2', 'Prinsesstårta', 'Bak', ['Fika', 'Middag'], 245, 'per styck', 1, 3, IMG.tarta,
      'Sockerkaksbotten, vaniljkräm, grädde och grön marsipan. Sex till åtta personer.'),
    i('as-3', 'Budapesttårta', 'Bak', ['Fika', 'Middag'], 260, 'per styck', 1, 3, IMG.dessert,
      'Hasselnötsmaräng, grädde och mandarin. Vår mest beställda tårta.'),
    i('as-4', 'Napoleonbakelser', 'Bak', ['Fika'], 46, 'per styck', 8, 2, IMG.bakverk,
      'Smördeg, vaniljkräm, glasyr. Levereras samma morgon som de bakas.'),
    i('as-5', 'Landgångsplanka', 'Mat', ['Lunch', 'Mellanmål'], 88, 'per person', 8, 3, IMG.lunch,
      'Landgångar i bitar på planka, blandade sorter, färdigt att ställa fram.'),
    i('as-6', 'Delikatessplanka', 'Snacks', ['Mellanmål', 'Middag'], 155, 'per person', 10, 4, IMG.mingel,
      'Chark, ost, inlagt och frukt. Plankan står kvar hos er över kvällen.'),
    i('as-7', 'Konferensfika, för- och eftermiddag', 'Bak', ['Fika', 'Mellanmål'], 115, 'per person', 12, 2, IMG.fika,
      'Två leveranser samma dag: bulle på morgonen, bakelse på eftermiddagen.'),
    i('as-8', 'Dagens sallad', 'Mat', ['Lunch'], 125, 'per person', 8, 2, IMG.gronsaker,
      'Säsongens sallad med bröd och smör. Kött, fisk eller vegetariskt.'),
    i('as-9', 'Belgiska våfflor', 'Bak', ['Mellanmål'], 52, 'per styck', 10, 3, IMG.bakverk,
      'Med sylt och grädde separat, så de inte blir blöta på vägen.'),
    i('as-10', 'Hantverksgelato', 'Snacks', ['Mellanmål', 'Middag'], 42, 'per person', 15, 4, IMG.dessert,
      'Fyra smaker i termolåda, skedar och skål ingår. Sommarsäsong.'),
  ],
  'kok-11': [
    i('k11-1', 'Säsongsbuffé', 'Mat', ['Middag'], 395, 'per person', 20, 7, IMG.buffe,
      'Fem rätter efter vad som finns just nu. Vi kommer med kock och dukar upp hos er.'),
    i('k11-2', 'Minglemat, sju tuggor', 'Snacks', ['Mellanmål', 'Middag'], 245, 'per person', 15, 5, IMG.mingel,
      'Sju små rätter som går att äta stående, med ett glas i andra handen.'),
    i('k11-3', 'Varm lunch till kontoret', 'Mat', ['Lunch'], 165, 'per person', 12, 3, IMG.lunch,
      'Dagens rätt i värmelåda, sallad och bröd. Vi hämtar kärlen dagen efter.'),
    i('k11-4', 'Långbord, tre rätter', 'Mat', ['Middag'], 545, 'per person', 16, 10, IMG.buffe,
      'Förrätt, varmrätt och dessert med servis på plats. Ni står för borden.'),
    i('k11-5', 'Soppa & bröd', 'Mat', ['Lunch'], 115, 'per person', 10, 3, IMG.lunch,
      'Säsongssoppa på termos med surdeg och smör. Vinterns mest beställda.'),
    i('k11-6', 'Grillbuffé', 'Mat', ['Middag'], 365, 'per person', 25, 7, IMG.buffe,
      'Vi tar med grillen. Kött, fisk och grönt, tre tillbehör och bröd.'),
    i('k11-7', 'Snittar till invigningen', 'Snacks', ['Mellanmål'], 38, 'per styck', 30, 5, IMG.mingel,
      'Fyra sorter, minst tre per gäst. Serveringsfat ingår.'),
    i('k11-8', 'Dryckespaket', 'Dryck', ['Middag'], 185, 'per person', 15, 7, IMG.ol,
      'Två glas vin eller öl per person, alkoholfritt likställt. Glas ingår.'),
    i('k11-9', 'Frukostmöte med varmrätt', 'Mat', ['Frukost'], 175, 'per person', 10, 4, IMG.frukost,
      'Ägg, bacon, bröd, yoghurt och kaffe. För möten som börjar sju.'),
    i('k11-10', 'Dessert i glas', 'Bak', ['Middag'], 85, 'per person', 15, 5, IMG.dessert,
      'Två sorter i portionsglas, klara att ställa fram. Inget efterarbete.'),
  ],
  'pm-och-vanner': [
    i('pm-1', 'Sittande middag, tre rätter', 'Mat', ['Middag'], 795, 'per person', 24, 14, IMG.buffe,
      'Full servis med kockar och servispersonal på plats. Dryckespaket kan läggas till.'),
    i('pm-2', 'Sittande middag, fem rätter', 'Mat', ['Middag'], 1150, 'per person', 20, 21, IMG.buffe,
      'Vår provsmakningsmeny hemma hos er. Kock, servis och porslin ingår.'),
    i('pm-3', 'Champagnemottagning', 'Dryck', ['Mellanmål', 'Middag'], 265, 'per person', 20, 10, IMG.mingel,
      'Ett glas vid ankomst med två tilltugg. Alkoholfritt alternativ finns alltid.'),
    i('pm-4', 'Canapéer, åtta sorter', 'Snacks', ['Mellanmål'], 320, 'per person', 25, 10, IMG.mingel,
      'Åtta sorter, tre per gäst. Bärs runt av vår personal.'),
    i('pm-5', 'Affärslunch, två rätter', 'Mat', ['Lunch'], 385, 'per person', 8, 5, IMG.lunch,
      'Levereras på porslin och hämtas efteråt. För lunchen som ska imponera.'),
    i('pm-6', 'Julbord', 'Mat', ['Middag'], 695, 'per person', 20, 21, IMG.buffe,
      'Vår version av det klassiska. Uppdukat hos er, bokas från september.'),
    i('pm-7', 'Vinpaket, tre glas', 'Dryck', ['Middag'], 385, 'per person', 15, 14, IMG.ol,
      'Utvalt till menyn av vår sommelier, ett glas per rätt.'),
    i('pm-8', 'Ostvagn', 'Snacks', ['Middag'], 165, 'per person', 15, 10, IMG.ost,
      'Sex ostar med tillbehör, rullas fram efter maten. Skyltar på svenska.'),
    i('pm-9', 'Petit four', 'Bak', ['Fika', 'Middag'], 95, 'per person', 15, 10, IMG.bakverk,
      'Fyra små bakverk till kaffet. Det där sista intrycket.'),
    i('pm-10', 'Frukostseminarium', 'Mat', ['Frukost'], 245, 'per person', 15, 7, IMG.frukost,
      'Uppdukat före åtta, avdukat före nio. Ni hinner köra hela mötet.'),
  ],
  'sorgardens-mejeri': [
    i('sg-1', 'Ostbricka från gården', 'Snacks', ['Mellanmål', 'Middag'], 145, 'per person', 8, 3, IMG.ost,
      'Sex gårdsostar, kex, honung och inlagt. Levereras på skiffer med skyltar.'),
    i('sg-2', 'Ostprovning med värd', 'Snacks', ['Mellanmål'], 275, 'per person', 12, 10, IMG.ost,
      'Vi kommer ut och berättar om åtta ostar. Tar drygt en timme.'),
    i('sg-3', 'Frukostost & smör', 'Mat', ['Frukost'], 65, 'per person', 10, 3, IMG.gard,
      'Nyskuren ost, gårdssmör och knäcke. Till er egen frukostleverans.'),
    i('sg-4', 'Grillost till buffén', 'Mat', ['Middag'], 88, 'per person', 15, 4, IMG.gard,
      'Halloumityp av gårdsmjölk, skuren och klar att lägga på grillen.'),
    i('sg-5', 'Yoghurt & granola', 'Mat', ['Frukost', 'Mellanmål'], 58, 'per person', 10, 3, IMG.frukost,
      'Turkisk yoghurt i portionsglas med granola och sylt separat.'),
    i('sg-6', 'Ostkaka med sylt', 'Bak', ['Fika', 'Middag'], 72, 'per person', 10, 4, IMG.dessert,
      'Småländsk ostkaka, varm eller kall, med hjortronsylt och grädde.'),
    i('sg-7', 'Mjölk & fil till kontoret', 'Dryck', ['Frukost'], 42, 'per person', 12, 3, IMG.gard,
      'Opastöriserad mjölk och fil i glasflaska. Flaskorna hämtas tillbaka.'),
    i('sg-8', 'Presentask med tre ostar', 'Snacks', ['Mellanmål'], 320, 'per styck', 1, 5, IMG.ost,
      'Tre ostar i träask med kort. Populär som julgåva till personalen.'),
    i('sg-9', 'Ostbord till festen', 'Snacks', ['Middag'], 195, 'per person', 25, 7, IMG.ost,
      'Tolv ostar uppdukade på plats med bröd, frukt och nötter.'),
    i('sg-10', 'Glass från gården', 'Bak', ['Mellanmål', 'Middag'], 48, 'per person', 15, 5, IMG.dessert,
      'Vaniljglass på gårdsgrädde i termolåda. Skedar och skålar ingår.'),
  ],
  gronsakslandet: [
    i('gl-1', 'Grönsaksfat med dipp', 'Snacks', ['Mellanmål'], 85, 'per person', 10, 2, IMG.gronsaker,
      'Säsongens grönsaker, råa och rostade, med två dippar. Helt växtbaserat.'),
    i('gl-2', 'Salladsbuffé', 'Mat', ['Lunch'], 155, 'per person', 12, 3, IMG.gronsaker,
      'Fyra sallader, bröd och dressing separat. Räcker som hel lunch.'),
    i('gl-3', 'Grönsakslådor till kontoret', 'Mat', ['Lunch'], 135, 'per person', 10, 3, IMG.lunch,
      'Varm växtbaserad rätt i låda. Byts varje vecka efter vad som skördas.'),
    i('gl-4', 'Rotfruktsgratäng', 'Mat', ['Middag'], 145, 'per person', 15, 4, IMG.buffe,
      'I ugnsform, klar att värma. Levereras med instruktion och tid.'),
    i('gl-5', 'Fruktkorg', 'Snacks', ['Frukost', 'Mellanmål'], 45, 'per person', 10, 2, IMG.gronsaker,
      'Säsongsfrukt, tvättad och klar. Korgen hämtas nästa leverans.'),
    i('gl-6', 'Smoothies i flaska', 'Dryck', ['Frukost', 'Mellanmål'], 55, 'per styck', 12, 3, IMG.gard,
      'Tre smaker i glasflaska, inget socker tillsatt. Kylvara.'),
    i('gl-7', 'Soppa på skörden', 'Mat', ['Lunch'], 105, 'per person', 10, 3, IMG.lunch,
      'Dagens grönsakssoppa på termos med bröd. Alltid växtbaserad.'),
    i('gl-8', 'Picklat & inlagt', 'Snacks', ['Mellanmål', 'Middag'], 65, 'per person', 12, 4, IMG.gard,
      'Fyra sorters inlagt till charken eller osten. Burkarna får ni behålla.'),
    i('gl-9', 'Grillgrönt till festen', 'Mat', ['Middag'], 95, 'per person', 20, 5, IMG.gronsaker,
      'Färdigmarinerat och skuret, klart att lägga direkt på gallret.'),
    i('gl-10', 'Örtkruka till borden', 'Snacks', ['Middag'], 75, 'per styck', 5, 4, IMG.gard,
      'Levande örter i kruka som dukning. Går att plantera om efteråt.'),
  ],
  'kafferosteriet-kalmar': [
    i('kk-1', 'Kaffe på termos', 'Dryck', ['Frukost', 'Fika'], 35, 'per person', 10, 1, IMG.kaffe,
      'Färskrostat bryggkaffe på tvålitersterminer, med muggar, mjölk och socker.'),
    i('kk-2', 'Barista på plats', 'Dryck', ['Fika', 'Mellanmål'], 195, 'per person', 25, 10, IMG.barista,
      'Vi kommer med maskin och barista. Latte, espresso och filter i tre timmar.'),
    i('kk-3', 'Kaffeprovning', 'Dryck', ['Mellanmål'], 245, 'per person', 12, 10, IMG.barista,
      'Fyra bönor, tre bryggmetoder och en som berättar. Cirka en timme.'),
    i('kk-4', 'Iskaffe på fat', 'Dryck', ['Lunch', 'Mellanmål'], 42, 'per person', 20, 5, IMG.kaffe,
      'Cold brew på tapp med is och glas. Sommarens mest beställda.'),
    i('kk-5', 'Te-sortiment', 'Dryck', ['Frukost', 'Fika'], 28, 'per person', 10, 2, IMG.kaffe,
      'Sex sorters löste med kannor och silar. För dem som inte dricker kaffe.'),
    i('kk-6', 'Bönor till kontoret', 'Dryck', ['Frukost'], 185, 'per styck', 2, 3, IMG.kaffe,
      'Kilopåse färskrostat, malet efter er maskin. Levereras löpande om ni vill.'),
    i('kk-7', 'Frukostkaffe med bulle', 'Dryck', ['Frukost'], 68, 'per person', 12, 2, IMG.frukost,
      'Termoskaffe och en bulle per person. Enklaste sättet att börja ett möte.'),
    i('kk-8', 'Chokladdryck', 'Dryck', ['Mellanmål'], 45, 'per person', 15, 3, IMG.kaffe,
      'Varm choklad på riktig choklad, inte pulver. Vinterleverans.'),
    i('kk-9', 'Kaffe & kaka, eftermiddag', 'Snacks', ['Mellanmål'], 78, 'per person', 12, 2, IMG.fika,
      'Termoskaffe och två småkakor per person. Levereras klockan två.'),
    i('kk-10', 'Espressobar till festen', 'Dryck', ['Middag'], 225, 'per person', 30, 14, IMG.barista,
      'Bar med två baristor efter maten. Går även på avec.'),
  ],
  'bryggeriet-i-skogen': [
    i('bs-1', 'Fatöl till festen', 'Dryck', ['Middag'], 3200, 'per styck', 30, 7, IMG.ol,
      'Trettiolitersfat med ofiltrerad lager, inklusive tapp och kylning. Alkoholfritt finns.'),
    i('bs-2', 'Ölprovning med bryggare', 'Dryck', ['Mellanmål'], 295, 'per person', 15, 14, IMG.ol,
      'Sex öl och den som bryggt dem. Tar knappt två timmar.'),
    i('bs-3', 'Flasköl, blandad låda', 'Dryck', ['Middag'], 65, 'per styck', 24, 5, IMG.ol,
      'Fyra sorter i trälåda. Lådan får ni behålla, flaskorna hämtar vi.'),
    i('bs-4', 'Alkoholfritt på fat', 'Dryck', ['Lunch', 'Middag'], 2400, 'per styck', 25, 7, IMG.ol,
      'Trettio liter alkoholfri lager. Samma tapp, ingen skillnad i glaset.'),
    i('bs-5', 'Läsk från bryggeriet', 'Dryck', ['Lunch', 'Mellanmål'], 38, 'per styck', 24, 5, IMG.gard,
      'Fläder, humle och citron. Sockrad med råsocker, inget konstigt.'),
    i('bs-6', 'Öl & ost', 'Snacks', ['Mellanmål', 'Middag'], 245, 'per person', 15, 10, IMG.ost,
      'Fyra öl mot fyra ostar från Sörgården. Vi tar med båda.'),
    i('bs-7', 'Bar på plats', 'Dryck', ['Middag'], 265, 'per person', 40, 21, IMG.ol,
      'Vi bygger bar, tappar och plockar ner. Personal och glas ingår.'),
    i('bs-8', 'Snacks till ölen', 'Snacks', ['Mellanmål'], 58, 'per person', 20, 5, IMG.mingel,
      'Saltat, rökt och friterat. Det som faktiskt passar till öl.'),
    i('bs-9', 'Julöl, säsong', 'Dryck', ['Middag'], 72, 'per styck', 24, 10, IMG.ol,
      'Mörk och kryddig, bryggd en gång om året. Slut när den är slut.'),
    i('bs-10', 'Presentlåda med tre öl', 'Dryck', ['Mellanmål'], 285, 'per styck', 1, 5, IMG.ol,
      'Tre flaskor och ett glas i låda. Vår vanligaste personalgåva.'),
  ],
  sjoboden: [
    i('sb-1', 'Skaldjursbord', 'Mat', ['Middag'], 455, 'per person', 12, 5, IMG.skaldjur,
      'Kräftor, räkor, musslor och bröd. Sommarsäsong, levereras i Västerviksområdet.'),
    i('sb-2', 'Räkmacka, stor', 'Mat', ['Lunch'], 165, 'per styck', 8, 3, IMG.lunch,
      'På surdeg, med ägg och majonnäs. Den vi är kända för.'),
    i('sb-3', 'Fisksoppa', 'Mat', ['Lunch', 'Middag'], 145, 'per person', 10, 3, IMG.lunch,
      'Saffran, dagens fisk och aioli. Levereras på termos med bröd.'),
    i('sb-4', 'Rökt fisk på fat', 'Snacks', ['Mellanmål', 'Middag'], 185, 'per person', 12, 4, IMG.skaldjur,
      'Lax, makrill och sik från vårt eget rökeri, med sås och citron.'),
    i('sb-5', 'Kräftskiva', 'Mat', ['Middag'], 395, 'per person', 15, 10, IMG.skaldjur,
      'Kräftor, västerbottenpaj, bröd och ost. Hattar och sånghäfte ingår.'),
    i('sb-6', 'Sillbricka', 'Snacks', ['Lunch', 'Middag'], 125, 'per person', 10, 5, IMG.mingel,
      'Fem sorters sill med potatis, ost och knäcke. Jul och midsommar.'),
    i('sb-7', 'Fisk & potatis, varmt', 'Mat', ['Lunch'], 175, 'per person', 12, 4, IMG.lunch,
      'Dagens fisk i värmelåda med potatis och sås. Kärlen hämtas dagen efter.'),
    i('sb-8', 'Toast skagen', 'Snacks', ['Mellanmål'], 68, 'per styck', 20, 4, IMG.mingel,
      'Klassisk skagenröra på rostat bröd. Görs på plats om ni vill.'),
    i('sb-9', 'Frukost vid vattnet', 'Mat', ['Frukost'], 155, 'per person', 10, 5, IMG.frukost,
      'Ägg, rökt fisk, bröd och kaffe. Levereras ner till bryggan.'),
    i('sb-10', 'Ostkaka & hjortron', 'Bak', ['Middag'], 78, 'per person', 12, 4, IMG.dessert,
      'Efter fisken. Varm ostkaka, hjortronsylt och grädde.'),
  ],
  'glasbrukets-matsal': [
    i('gm-1', 'Långbord i bruket', 'Mat', ['Middag'], 585, 'per person', 20, 14, IMG.buffe,
      'Sex rätter vid ett långbord. Trettiotvå stolar är max, sedan är det slut.'),
    i('gm-2', 'Gästkocksmiddag', 'Mat', ['Middag'], 745, 'per person', 16, 21, IMG.buffe,
      'Vi tar med en gästkock och bygger menyn runt kvällen.'),
    i('gm-3', 'Buffé på säsong', 'Mat', ['Lunch', 'Middag'], 345, 'per person', 20, 7, IMG.buffe,
      'Fyra varma och tre kalla rätter. Ställs fram, ingen servis behövs.'),
    i('gm-4', 'Lunch i matsalen, avhämtning', 'Mat', ['Lunch'], 135, 'per person', 8, 2, IMG.lunch,
      'Dagens rätt att hämta i lådor. Beställ före nio samma dag.'),
    i('gm-5', 'Konferenspaket, heldag', 'Mat', ['Frukost', 'Lunch', 'Fika'], 445, 'per person', 12, 7, IMG.frukost,
      'Frukost, förmiddagsfika, lunch och eftermiddagsfika. En rad på fakturan.'),
    i('gm-6', 'Kvällsmingel', 'Snacks', ['Mellanmål', 'Middag'], 225, 'per person', 20, 7, IMG.mingel,
      'Sex tuggor och två glas. För kvällen som inte ska bli en middag.'),
    i('gm-7', 'Vegetarisk meny, fyra rätter', 'Mat', ['Middag'], 495, 'per person', 15, 10, IMG.gronsaker,
      'Helt växtbaserat och inte ett kompromissalternativ. Provsmaka först om ni vill.'),
    i('gm-8', 'Fika i glasbruket', 'Bak', ['Fika'], 105, 'per person', 12, 3, IMG.fika,
      'Två bakverk, kaffe och te. Serveras i den gamla hyttan.'),
    i('gm-9', 'Julbord i bruket', 'Mat', ['Middag'], 645, 'per person', 20, 21, IMG.buffe,
      'Traditionellt med ett par egna påhitt. Bokas från oktober.'),
    i('gm-10', 'Dryckespaket, fyra glas', 'Dryck', ['Middag'], 425, 'per person', 15, 14, IMG.ol,
      'Öl, vin och alkoholfritt matchat till menyn, glas för glas.'),
  ],
  'vandrarens-kok': [
    i('vk-1', 'Eldkök på plats', 'Mat', ['Middag'], 425, 'per person', 15, 14, IMG.buffe,
      'Vi lagar över öppen eld där ni är. Tre rätter, vind och väder ingår.'),
    i('vk-2', 'Matsäck till utflykten', 'Mat', ['Lunch'], 145, 'per person', 8, 3, IMG.lunch,
      'Smörgås, frukt, kaka och dryck i papperspåse. Inget behöver kylas.'),
    i('vk-3', 'Svampvandring med lunch', 'Mat', ['Lunch'], 495, 'per person', 10, 21, IMG.gronsaker,
      'Vi plockar på förmiddagen och lagar det på eftermiddagen. Fyra timmar.'),
    i('vk-4', 'Fika i skogen', 'Bak', ['Fika'], 95, 'per person', 10, 5, IMG.fika,
      'Kaffe på elden och nybakat i korg. Filtar finns att låna.'),
    i('vk-5', 'Grillbuffé, vilt', 'Mat', ['Middag'], 385, 'per person', 20, 10, IMG.buffe,
      'Vilt från trakten, rotfrukter och bröd. Vi tar med både grill och ved.'),
    i('vk-6', 'Frukost i det fria', 'Mat', ['Frukost'], 165, 'per person', 10, 7, IMG.frukost,
      'Ägg och bacon i stekpanna över eld, bröd och kokkaffe.'),
    i('vk-7', 'Kokkaffe & kolbulle', 'Snacks', ['Mellanmål'], 78, 'per person', 12, 5, IMG.fika,
      'Kolbulle med lingon direkt ur pannan. Vinterns bästa paus.'),
    i('vk-8', 'Kickoff med matlagning', 'Mat', ['Lunch', 'Middag'], 545, 'per person', 12, 21, IMG.buffe,
      'Gruppen lagar maten, vi håller i det. Halvdag, avslutas med middag.'),
    i('vk-9', 'Termosdryck till skogen', 'Dryck', ['Mellanmål'], 42, 'per person', 12, 3, IMG.kaffe,
      'Kokkaffe, glögg eller saft på termos beroende på årstid.'),
    i('vk-10', 'Pannkakor över eld', 'Bak', ['Mellanmål'], 65, 'per person', 15, 7, IMG.dessert,
      'Raggmunk eller pannkaka med sylt. Barnen brukar äta tre.'),
  ],
};
